import BingoHeader from '../assets/headers/bingo.png';
import LeviathanHeader from '../assets/headers/leviathan.png';
import MarblesHeader from '../assets/headers/marbles.png';
import RaceDayHeader from '../assets/headers/raceday.png';
import BingoIcon from '../assets/icons/bingo.png';
import LeviathanIcon from '../assets/icons/leviathan.png';
import MarblesIcon from '../assets/icons/marble.png';
import RaceDayIcon from '../assets/icons/rdr.png';
import { GameInfo } from './lobby';
import { getStreamsixIGDBGames, UserProfile, Website } from './social';

export const BINGO_LICENSE = 'd2eea033-f28d-41c6-a992-f85a55c70ab2';
export const LEVIATHAN_LICENSE = 'a630ea40-eb3f-4b5f-b5e7-000bd8cde8de';
export const RACE_DAY_LICENSE = '5f71e4ab-0366-49d8-b49c-cfbeae8acebe';
export const MARBLES_LICENSE = '65b2f1af-8eaf-43ea-85b3-0e3ced18f237';

export const BINGO_NAME = 'The Bingo Room';
export const LEVIATHAN_NAME = 'Leviathan: Streams of Legends';
export const RACE_DAY_NAME = 'Race Day Rampage';
export const MARBLES_NAME = 'Marbles on Stream';

export const BINGO_DEEPLINK = '';
export const LEVIATHAN_DEEPLINK = 'tpleviathan';
export const RACE_DAY_DEEPLINK = '';
export const MARBLES_DEEPLINK = '';

export const BINGO_BLURB =
    'The Bingo Room is a stream interactive game designed for hosts to play with their audience. This game is part of the On Stream brand by Pixel by Pixel Studios.';
export const LEVIATHAN_BLURB =
    'Command your army (composed of your Twitch viewers) in this new legendary Social Real Time Strategy game. Bond with your audience in unprecedented ways where you control the war, while they fight each battle from their phones.';
export const RACE_DAY_BLURB =
    'Welcome to Race Day Rampage, a social arcade racing game built for streamers and their audience. In the game, you take on the role of Race Director. You pick the tracks, invite your audience and control the race. Cinematic camera controls let you zoom in on specific racers or areas, or zoom out and watch the race from afar. At the same time, the tracks have various traps that you can control to add to the chaos and influence the race!';
export const MARBLES_BLURB =
    'Marbles on Stream is a Marble Racing game for everyone! You can simulate races yourself or stream it on Twitch with your viewers!';

export const BINGO_DOWNLOAD = 'https://pixelbypixel.studio/about/the-bingo-room';
export const LEVIATHAN_DOWNLOAD = 'https://www.leviathanlegends.com/';
export const RACE_DAY_DOWNLOAD = 'https://www.racedayrampage.com/';
export const MARBLES_DOWNLOAD = 'https://www.pixelbypixelstudios.live/marbles-on-stream';

const BINGO_IGDB_GAME_ID = 143646;
const LEVIATHAN_IGDB_GAME_ID = 197255;
const RACE_DAY_IGDB_GAME_ID = 208312;
const MARBLES_IGDB_GAME_ID = 116975;

export interface LocalGameInfo {
    id: string;
    name: string;
    header: string;
    deeplink: string;
    blurb: string;
    downloadLink: string;
    websites: Website[];
}

export const getGameInfo = async (): Promise<LocalGameInfo[]> => {
    const gameData = await getStreamsixIGDBGames();

    const games = gameData.map((game) => {
        const license = getGameLicense(game.id);
        const deeplink = getDeeplink(license);
        const header = game.cover.url;

        const gameInfo: LocalGameInfo = {
            blurb: game.summary || getBlurb(license),
            deeplink: deeplink,
            downloadLink:
                // game.websites?.find((website) => website.category === 1)?.url || disabling this for now as the data on IGDB is out of date
                getDownload(license),
            header: header.replace('t_thumb', 't_1080p') || getHeaderImage(license)!,
            id: license,
            name: game.name || getName(license),
            websites: game.websites,
        };

        return gameInfo;
    });

    return games;
};

export const getBorderClassName = (license: string) => {
    switch (license) {
        case LEVIATHAN_LICENSE:
            return 'pink-border';
        case MARBLES_LICENSE:
            return 'cyan-border';
        case RACE_DAY_LICENSE:
            return 'yellow-border';
        case BINGO_LICENSE:
            return 'orange-border';
    }
};

export const getBackgroundClassName = (license: string) => {
    switch (license) {
        case LEVIATHAN_LICENSE:
            return 'pink-background';
        case MARBLES_LICENSE:
            return 'cyan-background';
        case RACE_DAY_LICENSE:
            return 'yellow-background';
        case BINGO_LICENSE:
            return 'orange-background';
    }
};

export const getHeaderImage = (license: string): string | undefined => {
    switch (license) {
        case LEVIATHAN_LICENSE:
            return LeviathanHeader;
        case MARBLES_LICENSE:
            return MarblesHeader;
        case RACE_DAY_LICENSE:
            return RaceDayHeader;
        case BINGO_LICENSE:
            return BingoHeader;
    }
};

export const getGameIcon = (license: string): string | undefined => {
    switch (license) {
        case LEVIATHAN_LICENSE:
            return LeviathanIcon;
        case MARBLES_LICENSE:
            return MarblesIcon;
        case RACE_DAY_LICENSE:
            return RaceDayIcon;
        case BINGO_LICENSE:
            return BingoIcon;
    }
};

export const isMarblesGame = (gameInfo?: GameInfo): boolean => {
    if (gameInfo) {
        return gameInfo.license === MARBLES_LICENSE;
    } else {
        return false;
    }
};

export const getS6ColourFromOnlineStatus = (onlineStatus: boolean | undefined) => {
    const colours = ['cyan', 'pink'];
    let colourIndex: number = 0;
    if (onlineStatus) {
        colourIndex = 0;
    } else {
        colourIndex = 1;
    }
    return colours[colourIndex];
};

export const getStreamUrl = (twitchLogin?: string): string => {
    if (twitchLogin) {
        return `https://www.twitch.tv/${twitchLogin}`;
    } else {
        return '';
    }
};

/**
 * @returns the Twitch viewer count if the user is live
 */
export const getViewerCount = (profile?: UserProfile): number | undefined =>
    profile?.twitch_stream?.viewer_count;

/**
 *
 * @returns the active player count as reported by the game through the actor info
 */
export const getActivePlayerCount = (gameInfo: GameInfo): number | undefined =>
    gameInfo.actorInfo?.data?.activePlayerCount;

const getGameLicense = (igdbGameID: number): string => {
    switch (igdbGameID) {
        case BINGO_IGDB_GAME_ID:
            return BINGO_LICENSE;
        case LEVIATHAN_IGDB_GAME_ID:
            return LEVIATHAN_LICENSE;
        case MARBLES_IGDB_GAME_ID:
            return MARBLES_LICENSE;
        case RACE_DAY_IGDB_GAME_ID:
            return RACE_DAY_LICENSE;
        default:
            return '';
    }
};

const getDeeplink = (license: string): string => {
    switch (license) {
        case BINGO_LICENSE:
            return BINGO_DEEPLINK;
        case LEVIATHAN_LICENSE:
            return LEVIATHAN_DEEPLINK;
        case MARBLES_LICENSE:
            return MARBLES_DEEPLINK;
        case RACE_DAY_LICENSE:
            return RACE_DAY_DEEPLINK;
        default:
            return '';
    }
};

const getDownload = (license: string): string => {
    switch (license) {
        case BINGO_LICENSE:
            return BINGO_DOWNLOAD;
        case LEVIATHAN_LICENSE:
            return LEVIATHAN_DOWNLOAD;
        case MARBLES_LICENSE:
            return MARBLES_DOWNLOAD;
        case RACE_DAY_LICENSE:
            return RACE_DAY_DOWNLOAD;
        default:
            return '';
    }
};

const getBlurb = (license: string): string => {
    switch (license) {
        case BINGO_LICENSE:
            return BINGO_BLURB;
        case LEVIATHAN_LICENSE:
            return LEVIATHAN_BLURB;
        case MARBLES_LICENSE:
            return MARBLES_BLURB;
        case RACE_DAY_LICENSE:
            return RACE_DAY_BLURB;
        default:
            return '';
    }
};

const getName = (license: string): string => {
    switch (license) {
        case BINGO_LICENSE:
            return BINGO_NAME;
        case LEVIATHAN_LICENSE:
            return LEVIATHAN_NAME;
        case MARBLES_LICENSE:
            return MARBLES_NAME;
        case RACE_DAY_LICENSE:
            return RACE_DAY_NAME;
        default:
            return '';
    }
};
